import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../hooks/useUserProfile';
import { createWithdrawalRequest, getUserWithdrawals, WITHDRAWAL_STATUS, PAYMENT_NETWORKS, WITHDRAWAL_LIMITS } from '../services/withdrawalService';
import { getNetworkDisplayName, getNetworkFee, validateWalletAddress } from '../utils/walletValidation';
import { getWithdrawalLimits } from '../services/withdrawalLimitService';
import { checkWithdrawalRestriction, getWithdrawalLimit } from '../services/withdrawalRestrictionService';
import CustomerServicePopup from './CustomerServicePopup';

function WithdrawPage({ onBack, onOpenVIPLevels }) {
  const { user } = useAuth();
  const { userProfile, loading: profileLoading } = useUserProfile(user?.uid);
  const [activeTab, setActiveTab] = useState('withdraw');
  const [step, setStep] = useState(1); // 1: Initiation, 2: Payment Selection, 3: Confirmation
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [transactionPassword, setTransactionPassword] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState(null);
  const [walletAddress, setWalletAddress] = useState('');
  const [addressError, setAddressError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [currentWithdrawal, setCurrentWithdrawal] = useState(null);
  const [lastStatus, setLastStatus] = useState(null);
  const refreshIntervalRef = useRef(null);
  const notificationShownRef = useRef(new Set());
  const [showCustomerService, setShowCustomerService] = useState(false);
  const [withdrawalLimits, setWithdrawalLimits] = useState({ minAmount: 100, maxAmount: 10000 });
  const [amountError, setAmountError] = useState('');
  const [amountValid, setAmountValid] = useState(false);
  const [withdrawalRestriction, setWithdrawalRestriction] = useState(null);
  
  const hasNegativeBalance = false; // Knife system removed

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Load withdrawal limits
    const loadLimits = async () => {
      try {
        const limits = await getWithdrawalLimits();
        setWithdrawalLimits(limits);
      } catch (error) {
        console.error('Error loading withdrawal limits:', error);
        // Use defaults on error
        setWithdrawalLimits({ minAmount: 100, maxAmount: 10000 });
      }
    };
    loadLimits();
  }, []);

  useEffect(() => {
    if (user?.uid) {
      loadWithdrawalHistory();
      
      // Auto-refresh every 30 seconds
      refreshIntervalRef.current = setInterval(() => {
        loadWithdrawalHistory();
      }, 30000);
      
      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [user?.uid]);

  useEffect(() => {
    // Find current active withdrawal (prioritize under review, then most recent)
    if (withdrawals.length > 0) {
      // Prioritize under review withdrawals, otherwise show most recent
      const underReview = withdrawals.find(w => w.status === WITHDRAWAL_STATUS.UNDER_REVIEW);
      const active = underReview || withdrawals[0];
      
      if (active) {
        const previousStatus = currentWithdrawal?.status;
        const previousId = currentWithdrawal?.id;
        
        setCurrentWithdrawal(active);
        
        // Check for status changes and show notification
        if (previousId && previousId === active.id && previousStatus && previousStatus !== active.status && !notificationShownRef.current.has(`${active.id}-${active.status}`)) {
          showStatusNotification(active);
          notificationShownRef.current.add(`${active.id}-${active.status}`);
        }
      } else {
        setCurrentWithdrawal(null);
      }
    } else {
      setCurrentWithdrawal(null);
    }
  }, [withdrawals]);

  const loadWithdrawalHistory = async () => {
    try {
      if (activeTab === 'history') {
        setLoadingHistory(true);
      }
      const history = await getUserWithdrawals(user.uid);
      setWithdrawals(history);
    } catch (err) {
      console.error('Error loading withdrawal history:', err);
      setError('Failed to load withdrawal history');
    } finally {
      if (activeTab === 'history') {
        setLoadingHistory(false);
      }
    }
  };

  const showStatusNotification = (withdrawal) => {
    let message = '';
    let type = 'info';
    
    if (withdrawal.status === WITHDRAWAL_STATUS.APPROVED) {
      message = `Your withdrawal of ${withdrawal.amount} USDT has been approved!`;
      type = 'success';
    } else if (withdrawal.status === WITHDRAWAL_STATUS.REJECTED) {
      message = `Your withdrawal of ${withdrawal.amount} USDT was rejected. Reason: ${withdrawal.rejectionReason || 'No reason provided'}`;
      type = 'danger';
    }
    
    if (message && 'Notification' in window && Notification.permission === 'granted') {
      new Notification('Withdrawal Status Update', {
        body: message,
        icon: '/logo192.png'
      });
    }
  };

  const requestNotificationPermission = () => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Real-time amount validation
  useEffect(() => {
    if (!withdrawAmount) {
      setAmountError('');
      setAmountValid(false);
      return;
    }

    const amount = parseFloat(withdrawAmount);
    const minAmount = withdrawalLimits.minAmount || 100;
    const maxAmount = withdrawalLimits.maxAmount || 10000;

    if (isNaN(amount) || amount <= 0) {
      setAmountError('Please enter a valid amount above $100');
      setAmountValid(false);
    } else if (amount < minAmount) {
      setAmountError(`Withdrawal amount must be at least $${minAmount}. Please enter a valid amount above $${minAmount}.`);
      setAmountValid(false);
    } else if (amount > maxAmount) {
      setAmountError(`Maximum withdrawal amount is $${maxAmount} USDT`);
      setAmountValid(false);
    } else {
      setAmountError('');
      setAmountValid(true);
    }
  }, [withdrawAmount, withdrawalLimits]);

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Check VIP level withdrawal restriction first
      if (withdrawalRestriction && !withdrawalRestriction.canWithdraw) {
        if (withdrawalRestriction.action === 'customer_care') {
          // VIP4 - Show customer service popup
          setShowCustomerService(true);
          setLoading(false);
          return;
        } else if (withdrawalRestriction.action === 'upgrade') {
          // VIP1-3 - Show upgrade popup and redirect
          const confirmed = window.confirm(
            `${withdrawalRestriction.message}\n\nWould you like to upgrade to ${withdrawalRestriction.nextLevel}?`
          );
          if (confirmed && onOpenVIPLevels) {
            onOpenVIPLevels();
          }
          setLoading(false);
          return;
        }
      }

      // Validate amount
      const amount = parseFloat(withdrawAmount);
      const minAmount = withdrawalLimits.minAmount || 100;
      const maxAmount = withdrawalLimits.maxAmount || 10000;

      if (!amount || isNaN(amount) || amount < minAmount) {
        throw new Error(`Withdrawal amount must be at least $${minAmount}. Please enter a valid amount above $${minAmount}.`);
      }

      if (amount > maxAmount) {
        throw new Error(`Maximum withdrawal amount is $${maxAmount} USDT`);
      }

      if (!transactionPassword) {
        throw new Error('Transaction password is required');
      }

      // Verify transaction password (this will be done in createWithdrawalRequest, but we can pre-verify)
      // For now, proceed to step 2
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNetworkSelect = (network) => {
    setSelectedNetwork(network);
    setWalletAddress('');
    setAddressError('');
  };

  const handleAddressChange = (e) => {
    const address = e.target.value;
    setWalletAddress(address);
    setAddressError('');

    if (address && selectedNetwork) {
      const validation = validateWalletAddress(address, selectedNetwork);
      if (!validation.valid) {
        setAddressError(validation.error);
      }
    }
  };

  const handleStep2Back = () => {
    setStep(1);
    setSelectedNetwork(null);
    setWalletAddress('');
    setAddressError('');
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    setError('');
    setAddressError('');

    if (!selectedNetwork) {
      setError('Please select a payment network');
      return;
    }

    if (!walletAddress.trim()) {
      setError('Wallet address is required');
      return;
    }

    // Validate address
    const validation = validateWalletAddress(walletAddress, selectedNetwork);
    if (!validation.valid) {
      setAddressError(validation.error);
      setError(validation.error);
      return;
    }

    setLoading(true);

    try {
      // Create withdrawal request
      await createWithdrawalRequest(user.uid, {
        amount: parseFloat(withdrawAmount),
        transactionPassword,
        paymentNetwork: selectedNetwork,
        walletAddress: walletAddress.trim()
      });

      setSuccess(true);
      setStep(3);
      
      // Reload history to show new withdrawal
      await loadWithdrawalHistory();
      
      // Reset form
      setTimeout(() => {
        setStep(1);
        setWithdrawAmount('');
        setTransactionPassword('');
        setSelectedNetwork(null);
        setWalletAddress('');
        setSuccess(false);
        setActiveTab('history'); // Switch to history tab to show status
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const formatDateShort = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const truncateAddress = (address) => {
    if (!address) return '';
    if (address.length <= 12) return address;
    return `${address.substring(0, 6)}...${address.substring(address.length - 6)}`;
  };

  const getExpectedCompletionTime = (createdAt) => {
    if (!createdAt) return 'N/A';
    try {
      const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
      const expectedTime = new Date(date.getTime() + 60 * 60 * 1000); // Add 1 hour
      return expectedTime.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const getBlockchainExplorerUrl = (network, hash) => {
    if (!hash) return null;
    const explorers = {
      'TRON_TRC20': `https://tronscan.org/#/transaction/${hash}`,
      'ETHEREUM_ERC20': `https://etherscan.io/tx/${hash}`,
      'BTC_BLOCKCHAIN': `https://www.blockchain.com/btc/tx/${hash}`
    };
    return explorers[network] || null;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      [WITHDRAWAL_STATUS.UNDER_REVIEW]: { class: 'warning', icon: 'bi-clock-history' },
      [WITHDRAWAL_STATUS.APPROVED]: { class: 'success', icon: 'bi-check-circle' },
      [WITHDRAWAL_STATUS.REJECTED]: { class: 'danger', icon: 'bi-x-circle' }
    };
    const config = statusConfig[status] || { class: 'secondary', icon: 'bi-circle' };
    return (
      <span className={`badge bg-${config.class}`}>
        <i className={`bi ${config.icon} me-1`}></i>
        {status}
      </span>
    );
  };

  const availableBalance = userProfile?.balances?.wallet || 0;
  const vipLevel = userProfile?.vipLevel || 'VIP1';

  // Check withdrawal restriction based on VIP level and balance
  useEffect(() => {
    if (userProfile && availableBalance !== undefined) {
      const restriction = checkWithdrawalRestriction(vipLevel, availableBalance);
      setWithdrawalRestriction(restriction);
    }
  }, [userProfile, availableBalance, vipLevel]);

  return (
    <div className="withdraw-page">
      {/* Top Navigation Bar */}
      <div className="withdraw-top-bar">
        <div className="withdraw-back-btn" onClick={onBack}>
          <i className="bi bi-chevron-left"></i>
        </div>
        <div className="withdraw-title-text">Withdraw</div>
        <div className="withdraw-spacer"></div>
      </div>

      {/* Tabbed Interface */}
      <div className="withdraw-tabs">
        <div 
          className={`withdraw-tab ${activeTab === 'withdraw' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('withdraw');
            setStep(1);
            setError('');
            setSuccess(false);
          }}
        >
          Withdraw
        </div>
        <div 
          className={`withdraw-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </div>
      </div>

      {/* Main Content */}
      <div className="withdraw-content">
        {activeTab === 'withdraw' ? (
          <>
            {step === 1 && (
              <>
                {/* Account Amount Banner */}
                <div className="withdraw-amount-banner" style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '12px',
                  padding: '24px',
                  marginBottom: '24px',
                  color: 'white',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                }}>
                  <div className="withdraw-amount-info">
                    <div className="withdraw-amount-label" style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '8px' }}>
                      Available Balance
                    </div>
                    <div className="withdraw-amount-value" style={{ fontSize: '2rem', fontWeight: 'bold', marginBottom: '8px' }}>
                      {profileLoading ? '...' : `${availableBalance.toFixed(2)} USDT`}
                    </div>
                    <div className="withdraw-amount-message" style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                      <i className="bi bi-clock me-1"></i>
                      You will receive your withdrawal within an hour
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}

                <form onSubmit={handleStep1Submit}>
                  {/* Withdraw Amount Input */}
                  <div className="withdraw-input-section" style={{ marginBottom: '20px' }}>
                    <div className="withdraw-input-label" style={{ marginBottom: '8px', fontWeight: '500' }}>
                      Withdraw Amount (USDT)
                    </div>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        className={`form-control ${amountError ? 'is-invalid' : amountValid ? 'is-valid' : ''}`}
                        placeholder={`Enter amount (min: $${withdrawalLimits.minAmount || 100})`}
                        value={withdrawAmount}
                        onChange={(e) => {
                          setWithdrawAmount(e.target.value);
                          setError('');
                        }}
                        min={withdrawalLimits.minAmount || 100}
                        max={withdrawalLimits.maxAmount || 10000}
                        step="0.01"
                        required
                        disabled={loading}
                        style={{ padding: '12px', fontSize: '1rem' }}
                      />
                      {amountValid && !amountError && (
                        <div className="valid-feedback" style={{ display: 'block' }}>
                          <i className="bi bi-check-circle me-1"></i>
                          Amount is valid
                        </div>
                      )}
                      {amountError && (
                        <div className="invalid-feedback" style={{ display: 'block' }}>
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          {amountError}
                        </div>
                      )}
                    </div>
                    <small className="text-muted" style={{ marginTop: '4px', display: 'block' }}>
                      <strong>Minimum withdrawal: ${withdrawalLimits.minAmount || 100}</strong>
                      {withdrawalLimits.maxAmount && (
                        <span> | Maximum: ${withdrawalLimits.maxAmount} USDT</span>
                      )}
                    </small>
                  </div>

                  {/* Transaction Password Input */}
                  <div className="withdraw-input-section" style={{ marginBottom: '24px' }}>
                    <div className="withdraw-input-label" style={{ marginBottom: '8px', fontWeight: '500' }}>
                      Transaction Password
                    </div>
                    <input
                      type="password"
                      className="form-control"
                      placeholder="Enter your transaction password"
                      value={transactionPassword}
                      onChange={(e) => {
                        setTransactionPassword(e.target.value);
                        setError('');
                      }}
                      required
                      disabled={loading}
                      style={{ padding: '12px', fontSize: '1rem' }}
                    />
                  </div>

                  {/* Withdraw Button */}
                  <button 
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={
                      loading || 
                      profileLoading || 
                      !withdrawAmount || 
                      !transactionPassword || 
                      hasNegativeBalance || 
                      !amountValid || 
                      !!amountError ||
                      (withdrawalRestriction && !withdrawalRestriction.canWithdraw)
                    }
                    style={{ 
                      padding: '12px', 
                      fontSize: '1rem', 
                      fontWeight: '500',
                      backgroundColor: (
                        hasNegativeBalance || 
                        !amountValid || 
                        !!amountError || 
                        (withdrawalRestriction && !withdrawalRestriction.canWithdraw)
                      ) ? '#6c757d' : '#745CB6',
                      borderColor: (
                        hasNegativeBalance || 
                        !amountValid || 
                        !!amountError || 
                        (withdrawalRestriction && !withdrawalRestriction.canWithdraw)
                      ) ? '#6c757d' : '#745CB6',
                      opacity: (
                        hasNegativeBalance || 
                        !amountValid || 
                        !!amountError || 
                        (withdrawalRestriction && !withdrawalRestriction.canWithdraw)
                      ) ? 0.6 : 1,
                      cursor: (
                        hasNegativeBalance || 
                        !amountValid || 
                        !!amountError || 
                        (withdrawalRestriction && !withdrawalRestriction.canWithdraw)
                      ) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Verifying...
                      </>
                    ) : (withdrawalRestriction && !withdrawalRestriction.canWithdraw) ? (
                      <>
                        <i className="bi bi-lock me-2"></i>
                        Withdrawal Restricted
                      </>
                    ) : (
                      <>
                        Continue <i className="bi bi-arrow-right ms-2"></i>
                      </>
                    )}
                  </button>
                </form>
              </>
            )}

            {step === 2 && (
              <>
                <div className="mb-3">
                  <button 
                    className="btn btn-link text-decoration-none p-0"
                    onClick={handleStep2Back}
                  >
                    <i className="bi bi-chevron-left me-1"></i>
                    Back
                  </button>
                </div>

                <div className="mb-4">
                  <h5>Select Payment Network</h5>
                  <p className="text-muted">Choose your preferred cryptocurrency network</p>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}

                <form onSubmit={handleStep2Submit}>
                  {/* Network Selection */}
                  <div className="mb-4">
                    <div className="row g-3">
                      {Object.values(PAYMENT_NETWORKS).map((network) => (
                        <div key={network} className="col-12">
                          <div
                            className={`card ${selectedNetwork === network ? 'border-primary' : ''}`}
                            style={{
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              borderWidth: selectedNetwork === network ? '2px' : '1px'
                            }}
                            onClick={() => handleNetworkSelect(network)}
                          >
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h6 className="mb-1">{getNetworkDisplayName(network)}</h6>
                                  <small className="text-muted">
                                    Network fee: ~{getNetworkFee(network)} {network === 'BTC_BLOCKCHAIN' ? 'BTC' : 'USDT'}
                                  </small>
                                </div>
                                {selectedNetwork === network && (
                                  <i className="bi bi-check-circle-fill text-primary" style={{ fontSize: '1.5rem' }}></i>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Wallet Address Input */}
                  {selectedNetwork && (
                    <div className="mb-4">
                      <label className="form-label" style={{ fontWeight: '500' }}>
                        Wallet Address ({getNetworkDisplayName(selectedNetwork)})
                      </label>
                      <input
                        type="text"
                        className={`form-control ${addressError ? 'is-invalid' : ''}`}
                        placeholder={`Enter your ${getNetworkDisplayName(selectedNetwork)} wallet address`}
                        value={walletAddress}
                        onChange={handleAddressChange}
                        required
                        disabled={loading}
                        style={{ padding: '12px', fontSize: '1rem', fontFamily: 'monospace' }}
                      />
                      {addressError && (
                        <div className="invalid-feedback">{addressError}</div>
                      )}
                      <small className="text-muted">
                        {selectedNetwork === 'TRON_TRC20' && 'TRON addresses start with "T" and are 34 characters long'}
                        {selectedNetwork === 'ETHEREUM_ERC20' && 'Ethereum addresses start with "0x" and are 42 characters long'}
                        {selectedNetwork === 'BTC_BLOCKCHAIN' && 'Bitcoin addresses start with "1", "3", or "bc1"'}
                      </small>
                    </div>
                  )}

                  {/* Confirm Button */}
                  <button 
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={loading || !selectedNetwork || !walletAddress.trim() || !!addressError}
                    style={{ padding: '12px', fontSize: '1rem', fontWeight: '500' }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Processing...
                      </>
                    ) : (
                      <>
                        Confirm Withdrawal <i className="bi bi-check-circle ms-2"></i>
                      </>
                    )}
                  </button>
                </form>
              </>
            )}

            {step === 3 && success && (
              <div className="text-center py-5">
                <div className="mb-4">
                  <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                </div>
                <h4 className="mb-3">Withdrawal Request Submitted!</h4>
                <p className="text-muted mb-4">
                  Your withdrawal request of <strong>{withdrawAmount} USDT</strong> is now under review.
                  <br />
                  You will receive your funds within an hour.
                </p>
                <p className="text-muted">
                  Status: <span className="badge bg-warning">Under Review</span>
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="withdraw-history-content">
            {/* Current Withdrawal Status Card - Only in History Tab */}
            {currentWithdrawal && (
              <div className="card mb-4 border-0 shadow-sm" style={{ 
                borderLeft: currentWithdrawal.status === WITHDRAWAL_STATUS.UNDER_REVIEW ? '4px solid #ffc107' :
                           currentWithdrawal.status === WITHDRAWAL_STATUS.APPROVED ? '4px solid #198754' :
                           '4px solid #dc3545'
              }}>
                <div className="card-header bg-transparent border-0 pb-0 d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    Current Withdrawal Status
                  </h5>
                  {currentWithdrawal.status === WITHDRAWAL_STATUS.UNDER_REVIEW && (
                    <small className="text-muted">
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Auto-refreshing every 30s
                    </small>
                  )}
                </div>
                <div className="card-body">
                  {currentWithdrawal.status === WITHDRAWAL_STATUS.UNDER_REVIEW && (
                    <div>
                      <div className="d-flex align-items-center mb-3">
                        <span className="badge bg-warning me-2" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                          🟡 Under Review
                        </span>
                      </div>
                      <p className="mb-2">
                        Your withdrawal of <strong>{currentWithdrawal.amount} USDT</strong> is currently under review
                      </p>
                      <div className="row text-muted small mb-2">
                        <div className="col-6">
                          <strong>Submitted:</strong> {formatDateShort(currentWithdrawal.createdAt)}, {formatTime(currentWithdrawal.createdAt)}
                        </div>
                        <div className="col-6">
                          <strong>Network:</strong> {getNetworkDisplayName(currentWithdrawal.paymentNetwork)}
                        </div>
                      </div>
                      <div className="alert alert-info py-2 mb-0">
                        <i className="bi bi-clock me-2"></i>
                        <strong>Expected completion:</strong> Within 1 hour (by {getExpectedCompletionTime(currentWithdrawal.createdAt)})
                      </div>
                    </div>
                  )}
                  
                  {currentWithdrawal.status === WITHDRAWAL_STATUS.APPROVED && (
                    <div>
                      <div className="d-flex align-items-center mb-3">
                        <span className="badge bg-success me-2" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                          🟢 Approved ✅
                        </span>
                      </div>
                      <p className="mb-2">
                        Your withdrawal of <strong>{currentWithdrawal.amount} USDT</strong> has been approved
                      </p>
                      <div className="row text-muted small mb-2">
                        <div className="col-6">
                          <strong>Completed:</strong> {formatDateShort(currentWithdrawal.processedAt || currentWithdrawal.updatedAt)}, {formatTime(currentWithdrawal.processedAt || currentWithdrawal.updatedAt)}
                        </div>
                        <div className="col-6">
                          <strong>Network:</strong> {getNetworkDisplayName(currentWithdrawal.paymentNetwork)}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {currentWithdrawal.status === WITHDRAWAL_STATUS.REJECTED && (
                    <div>
                      <div className="d-flex align-items-center mb-3">
                        <span className="badge bg-danger me-2" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                          🔴 Rejected ❌
                        </span>
                      </div>
                      <p className="mb-2">
                        Your withdrawal of <strong>{currentWithdrawal.amount} USDT</strong> was rejected
                      </p>
                      {currentWithdrawal.rejectionReason && (
                        <div className="alert alert-danger py-2 mb-2">
                          <strong>Reason:</strong> {currentWithdrawal.rejectionReason}
                        </div>
                      )}
                      <div className="alert alert-warning py-2 mb-0">
                        <i className="bi bi-info-circle me-2"></i>
                        Contact support if you have questions
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-3 pt-3 border-top">
                    <div className="row small text-muted">
                      <div className="col-12 col-md-6 mb-2">
                        <strong>Amount:</strong> {currentWithdrawal.amount} USDT
                      </div>
                      <div className="col-12 col-md-6 mb-2">
                        <strong>Wallet Address:</strong>
                        <div style={{ 
                          wordBreak: 'break-all', 
                          fontFamily: 'monospace', 
                          fontSize: '0.85rem',
                          marginTop: '4px'
                        }}>
                          {currentWithdrawal.walletAddress}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                <i className="bi bi-clock-history me-2"></i>
                Withdrawal History
              </h5>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={loadWithdrawalHistory}
                disabled={loadingHistory}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </button>
            </div>

            {loadingHistory ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : withdrawals.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#ccc' }}></i>
                  <p className="text-muted mt-3">No withdrawal history available</p>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Network</th>
                          <th>Status</th>
                          <th>Admin Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {withdrawals.map((withdrawal) => (
                          <tr key={withdrawal.id}>
                            <td>
                              <div>
                                <div>{formatDateShort(withdrawal.createdAt)}</div>
                                <small className="text-muted">{formatTime(withdrawal.createdAt)}</small>
                              </div>
                            </td>
                            <td>
                              <strong>{withdrawal.amount} USDT</strong>
                            </td>
                            <td>
                              <span className="badge bg-info">
                                {getNetworkDisplayName(withdrawal.paymentNetwork)}
                              </span>
                            </td>
                            <td>{getStatusBadge(withdrawal.status)}</td>
                            <td>
                              {withdrawal.rejectionReason ? (
                                <div className="text-danger small">
                                  <strong>Rejected:</strong> {withdrawal.rejectionReason}
                                </div>
                              ) : withdrawal.adminNotes ? (
                                <div className="text-muted small">{withdrawal.adminNotes}</div>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Customer Service Popup */}
      {showCustomerService && (
        <CustomerServicePopup onClose={() => setShowCustomerService(false)} />
      )}
    </div>
  );
}

export default WithdrawPage;

